import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type DecodeType = 'json' | 'boolean' | 'number' | 'string';

type ParamConfig<T> = {
  decode?: DecodeType;
  encode?: (value: T) => string;
  default?: T;
};

type Configs<TValues extends Record<string, unknown>> = Partial<{
  [K in keyof TValues]: ParamConfig<TValues[K]>;
}>;

export type State<TValues> = {
  [K in keyof TValues]: TValues[K] | undefined;
};

type URLStateNavigateOptions = {
  replace?: boolean;
};

type API<TValues> = {
  get: <K extends keyof TValues>(key: K) => TValues[K] | undefined;
  set: (patch: Partial<TValues>, options?: URLStateNavigateOptions) => void;
  remove: (keys?: (keyof TValues)[], options?: URLStateNavigateOptions) => void;
  reset: (keys?: (keyof TValues)[], options?: URLStateNavigateOptions) => void;
};

function decodeValue<T>(raw: string, decode?: DecodeType): T | undefined {
  try {
    switch (decode) {
      case 'json':
        return JSON.parse(raw);
      case 'boolean':
        return (raw === 'true') as T;
      case 'number':
        return Number(raw) as T;
      default:
        return raw as T;
    }
  } catch {
    return undefined;
  }
}

function encodeValue<T>(value: T, config?: ParamConfig<T>): string {
  if (config?.encode) return config.encode(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function isEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a === 'object' && typeof b === 'object' && a && b)
    return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

function parseState<TValues extends Record<string, unknown>>(
  searchParams: URLSearchParams,
  configs: Configs<TValues> | undefined,
): State<TValues> {
  const obj = {} as State<TValues>;
  const keys = Array.from(
    new Set([
      ...Object.keys(configs ?? {}),
      ...Array.from(searchParams.keys()),
    ]),
  ) as (keyof TValues)[];

  for (const key of keys) {
    const raw = searchParams.get(key as string);
    const config = configs?.[key];
    obj[key] =
      raw == null
        ? config?.default
        : (decodeValue<TValues[typeof key]>(raw, config?.decode) ??
          config?.default);
  }
  return obj;
}

export function createURLState<TValues extends Record<string, unknown>>(
  configs?: Configs<TValues>,
) {
  function useURLState<R>(options: {
    selector: (state: State<TValues>) => R;
    configs?: Configs<TValues>;
  }): [R, API<TValues>];

  function useURLState(options?: {
    selector?: undefined;
    configs?: Configs<TValues>;
  }): [State<TValues>, API<TValues>];

  function useURLState<R>(options?: {
    selector?: (state: State<TValues>) => R;
    configs?: Configs<TValues>;
  }): [State<TValues> | R, API<TValues>] {
    const [searchParams, setSearchParams] = useSearchParams();

    const mergedConfigs = { ...configs, ...options?.configs };
    const state = parseState(searchParams, mergedConfigs);
    const value = options?.selector ? options.selector(state) : state;

    const get = useCallback(
      <K extends keyof TValues>(key: K): TValues[K] | undefined => {
        return parseState(new URLSearchParams(window.location.search), configs)[
          key
        ];
      },
      [],
    );

    const set = useCallback(
      (patch: Partial<TValues>, options?: URLStateNavigateOptions) => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            for (const key in patch) {
              const val = patch[key];
              const config = configs?.[key as keyof TValues];
              if (val == null || val === '' || isEqual(val, config?.default)) {
                next.delete(key);
              } else {
                next.set(key, encodeValue(val, config as ParamConfig<unknown>));
              }
            }
            return next;
          },
          { replace: options?.replace ?? true },
        );
      },
      [setSearchParams],
    );

    const remove = useCallback(
      (keys?: (keyof TValues)[], options?: URLStateNavigateOptions) => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (!keys) Array.from(next.keys()).forEach((k) => next.delete(k));
            else keys.forEach((k) => next.delete(k as string));
            return next;
          },
          { replace: options?.replace ?? true },
        );
      },
      [setSearchParams],
    );

    const reset = useCallback(
      (keys?: (keyof TValues)[], options?: URLStateNavigateOptions) => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            const targetKeys =
              keys ?? (Object.keys(configs ?? {}) as (keyof TValues)[]);
            for (const k of targetKeys) {
              const config = configs?.[k];
              if (config?.default !== undefined) {
                next.set(
                  k as string,
                  encodeValue(config.default, config as ParamConfig<unknown>),
                );
              } else {
                next.delete(k as string);
              }
            }
            return next;
          },
          { replace: options?.replace ?? true },
        );
      },
      [setSearchParams],
    );

    const api = useMemo<API<TValues>>(
      () => ({ get, set, remove, reset }),
      [get, set, remove, reset],
    );

    return [value, api];
  }

  return useURLState;
}